// The candidate screens' extension slots (lane contract C-B10-13): where another lane mounts into lane B3's candidate
// profile timeline and the Candidates table without editing them (B10 S18's spine timeline, B10's saved views). Each
// slot is filled at import from the mounting lane's own `views/<lane>/register.ts` and read at render; the registry
// itself is lane B2's `createSlot` (views/requisition/slots.ts), so a duplicate key throws at import naming both lanes.
//
//   profileTimelineBody   registerProfileTimelineBody(def)   one occupant: replaces the profile's Candidate timeline
//                                                            tab body (#candidates/<cand_…>/timeline)
//   candidateColumns      registerCandidateColumn(def)       a column of the Candidates table, after Owner
//   candidateSavedViews   registerCandidateSavedViews(def)   one occupant: the saved-views control in its header
//
// Without a registration both screens are unchanged. Headers are his words, from the mounting lane's labels module.

import type { ComponentType, ReactNode } from "react";
import type { SearchRow } from "@/api/tools";
import { createSlot, type SavedViewsProps, type SlotEntry } from "@/views/requisition/slots";

// ---- the profile's Candidate timeline tab -----------------------------------------------------------------------------
export interface ProfileTimelineBodyProps {
  /** The profile's Candidate ID (the tab is drawn only where the reader may see it). */
  candidateId: string;
}

export interface ProfileTimelineBodyDef extends SlotEntry {
  readonly component: ComponentType<ProfileTimelineBodyProps>;
}

export const profileTimelineBody = createSlot<ProfileTimelineBodyDef>("candidate timeline body", { single: true });
export const registerProfileTimelineBody = (def: ProfileTimelineBodyDef): void => profileTimelineBody.register(def);

// ---- the Candidates table ---------------------------------------------------------------------------------------------
/** A row the reader may see: `cell` is never called for a masked row, which draws one masked cell across every column. */
export type CandidateListRow = SearchRow;

export interface CandidateColumnDef extends SlotEntry {
  /** His word for the column header. */
  readonly header: string;
  readonly cell: (row: CandidateListRow) => ReactNode;
  /** Whether this viewer sees the column (a manifest tool, never a role); default always. */
  readonly visible?: ((can: (tool: string) => boolean) => boolean) | undefined;
}

export const candidateColumns = createSlot<CandidateColumnDef>("candidate list column");
export const registerCandidateColumn = (def: CandidateColumnDef): void => candidateColumns.register(def);

export interface CandidateSavedViewsDef extends SlotEntry {
  /** `query` is the table's search and filters as flags (q, status, internal); `apply` lands on a saved one. */
  readonly component: ComponentType<SavedViewsProps>;
}

export const candidateSavedViews = createSlot<CandidateSavedViewsDef>("candidate saved views", { single: true });
export const registerCandidateSavedViews = (def: CandidateSavedViewsDef): void => candidateSavedViews.register(def);
